import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, ScanLine, BarChart3, HandHelping, HandCoins, Gift, Mail, FileText, Images, LogOut, ExternalLink, Store } from "lucide-react";
import { api } from "@/lib/api";
import AdminLogin from "./AdminLogin";
import Dashboard from "./Dashboard";
import { RegistrationsPage, VolunteersPage, ContactsPage, DonationsPage, GiveawayPage } from "./RecordPages";
import CheckIn from "./CheckIn";
import { VendorsPage } from "./VendorsPage";
import Attendance from "./Attendance";
import ContentEditor from "./ContentEditor";
import ImagesManager from "./ImagesManager";
import PrintSheet from "./PrintSheet";

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["registrations", "Registrants", Users],
  ["checkin", "Check-in", ScanLine],
  ["attendance", "Attendance", BarChart3],
  ["volunteers", "Volunteers", HandHelping],
  ["donations", "Donations", HandCoins],
  ["giveaway", "Giveaway", Gift],
  ["vendors", "Vendors", Store],
  ["contacts", "Contacts", Mail],
  ["content", "Content", FileText],
  ["images", "Images", Images],
];

const linkCls = ({ isActive }) =>
  `flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-amber-500 text-neutral-950" : "text-neutral-400 hover:bg-neutral-900 hover:text-neutral-100"
  }`;

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    document.title = "Admin · Egúngún Festival 2026";
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) return <div className="min-h-screen bg-neutral-950" data-testid="admin-loading" />;
  if (!user) return <AdminLogin onLogin={setUser} />;
  if (location.pathname.startsWith("/admin/print")) return <PrintSheet />;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 lg:flex" data-testid="admin-shell">
      <aside className="sticky top-0 z-30 border-b border-neutral-900 bg-neutral-950/95 backdrop-blur-md lg:h-screen lg:w-60 lg:shrink-0 lg:border-b-0 lg:border-r">
        <div className="kente kente-thin" />
        <div className="flex items-center justify-between px-4 py-3 lg:block lg:py-6">
          <Link to="/admin" className="block" data-testid="admin-brand">
            <span className="font-display text-lg font-bold text-gold">Egúngún</span>{" "}
            <span className="font-script text-red-500">Admin</span>
          </Link>
          <div className="flex items-center gap-1 lg:mt-4">
            <a href="/" target="_blank" rel="noreferrer" className="rounded-md p-2 text-neutral-400 hover:text-amber-400" data-testid="admin-view-site" title="View site">
              <ExternalLink className="h-4 w-4" />
            </a>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-md p-2 text-sm text-neutral-400 hover:text-red-400" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> <span className="hidden lg:inline">Log out</span>
            </button>
          </div>
        </div>
        <nav className="no-scrollbar flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:px-3">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={label} to={to ? `/admin/${to}` : "/admin"} end={!to} className={linkCls} data-testid={`admin-nav-${label.toLowerCase()}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-8 lg:py-10">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="registrations" element={<RegistrationsPage />} />
          <Route path="checkin" element={<CheckIn />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="volunteers" element={<VolunteersPage />} />
          <Route path="donations" element={<DonationsPage />} />
          <Route path="giveaway" element={<GiveawayPage />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="content" element={<ContentEditor />} />
          <Route path="images" element={<ImagesManager />} />
        </Routes>
      </main>
    </div>
  );
}

export const PageTitle = ({ title, children }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <h1 className="font-display text-3xl font-bold text-gold sm:text-4xl" data-testid="admin-page-title">{title}</h1>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
